import React, { useEffect, useState } from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { now } from "moment/moment";
import { StringParam, useQueryParam } from "use-query-params";
import { useParams } from "react-router";
import { useSelector } from "react-redux";
import { Table, Tabs } from "antd";
import { message } from "components";
import { RootState, useAppDispatch } from "app/store";
import {
  AssignmentListParams,
  RedemptionListParams,
} from "types/dto/request/assignment";
import { DEFAULT_PAGINATION } from "constants/common";
import {
  fetchAssignmentList,
  fetchRedemptionList,
  voucherGroupsSelectors,
} from "../../voucherGroupsSlice";
import MasterToolbar from "./MasterToolbar";
import useColumns from "../useColumns";
import styles from "./MasterVoucherAssignment.module.scss";
import RenewLicenseModal from "../RenewLicenseModal";

interface IProps {
  onRefreshSummary: () => void;
}

function MasterVoucherAssignment({ onRefreshSummary }: IProps) {
  const dispatch = useAppDispatch();
  const { customerGroupId, voucherGroupId } = useParams<{
    customerGroupId: string;
    voucherGroupId: string;
  }>();
  const [redeemerEmail] = useQueryParam("redeemerEmail", StringParam);
  const adminGroupId = parseInt(customerGroupId);
  const { voucherSummary } = useSelector(
    (state: RootState) => state.voucherGroups
  );
  const [email, setEmail] = useState(redeemerEmail || "");
  const { expirationDate, isOpenEnded } = voucherSummary;
  const isVoucherExpired = !isOpenEnded && expirationDate < now();

  const initialFilterState = {
    ...DEFAULT_PAGINATION,
    voucherGroupId: voucherGroupId,
    adminGroupId: adminGroupId,
  };

  const resetFilter = () => {
    setEmail("");
    setAssignmentFilter(initialFilterState);
    setRedemptionFilter(initialFilterState);
  };

  const {
    masterRedemptionColumnConfig,
    masterAssignmentColumnConfig,
  } = useColumns(
    "MASTER",
    voucherGroupId,
    adminGroupId,
    isVoucherExpired,
    resetFilter
  );

  const [assignmentFilter, setAssignmentFilter] = useState<
    AssignmentListParams
  >(initialFilterState);

  const [redemptionFilter, setRedemptionFilter] = useState<
    RedemptionListParams
  >({ ...initialFilterState, redeemerEmail: email.trim() });

  const voucherGroup = useSelector((state: RootState) =>
    voucherGroupsSelectors.selectById(state, voucherGroupId)
  );

  const [showRenewModal, setShowRenewModal] = useState(false);

  const {
    currentPage,
    currentSize,
    totalAssignElements,
    totalRedeemElements,
    redemptionList,
    assignmentList,
  } = useSelector((state: RootState) => state.voucherGroups);

  useEffect(() => {
    dispatch(fetchRedemptionList(redemptionFilter))
      .then(unwrapResult)
      .catch(message.error);
  }, [dispatch, redemptionFilter]);

  useEffect(() => {
    dispatch(fetchAssignmentList(assignmentFilter))
      .then(unwrapResult)
      .catch(message.error);
  }, [dispatch, assignmentFilter]);

  const handleAssignPaginationChange = (page: number, pageSize?: number) => {
    setAssignmentFilter((prevState) => ({
      ...prevState,
      page: page - 1,
      size: pageSize,
    }));
  };

  const handleRedeemedPaginationChange = (page: number, pageSize?: number) => {
    setRedemptionFilter((prevState) => ({
      ...prevState,
      page: page - 1,
      size: pageSize,
    }));
  };

  const onRenewClick = () => {
    if (voucherGroup?.hasExpired) {
      message.error("The code has expired. Please contact admin for support.");
      return;
    }

    if (
      voucherGroup?.totalAmount &&
      voucherGroup?.totalAmount - (voucherGroup?.assignedAmount ?? 0) < 1
    ) {
      message.error("No available licenses.Please contact admin for support..");
      return;
    }
    setShowRenewModal(true);
  };

  return (
    <>
      <Tabs
        defaultActiveKey={redeemerEmail ? "2" : "1"}
        type="card"
        className={styles.tab}
        onChange={resetFilter}
      >
        <Tabs.TabPane tab="Assignment status" key="1">
          <MasterToolbar
            filter={assignmentFilter}
            setFilter={setAssignmentFilter}
            email={email}
            setEmail={setEmail}
            resetFilter={resetFilter}
            onRenewClick={onRenewClick}
          />
          <Table
            className={styles.assignTable}
            rowKey={(record) => record.assignmentId}
            dataSource={assignmentList}
            columns={masterAssignmentColumnConfig}
            pagination={{
              showTotal: (total, range) =>
                `${range[0]}~${range[1]} of ${total} items`,
              total: totalAssignElements,
              current: currentPage,
              pageSize: currentSize,
              showSizeChanger: true,
              onChange: handleAssignPaginationChange,
            }}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab="Redemption status" key="2">
          <MasterToolbar
            isRedeem
            filter={redemptionFilter}
            setFilter={setRedemptionFilter}
            email={email}
            setEmail={setEmail}
            resetFilter={resetFilter}
            onRenewClick={onRenewClick}
          />
          <Table
            rowKey={(record) => record.redemptionId}
            dataSource={redemptionList}
            columns={masterRedemptionColumnConfig}
            pagination={{
              showTotal: (total, range) =>
                `${range[0]}~${range[1]} of ${total} items`,
              total: totalRedeemElements,
              current: currentPage,
              pageSize: currentSize,
              showSizeChanger: true,
              onChange: handleRedeemedPaginationChange,
            }}
          />
        </Tabs.TabPane>
      </Tabs>
      <RenewLicenseModal
        visible={showRenewModal}
        onClose={(shouldRefresh) => {
          setShowRenewModal(false);
          if (shouldRefresh) {
            onRefreshSummary();
            resetFilter();
          }
        }}
      />
    </>
  );
}

export default MasterVoucherAssignment;
